import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/format";

const ALL = "__all__";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border bg-muted/40 p-3">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p data-testid={testid} className="font-num text-xl font-bold">{fmt(value, "num")}</p>
  </div>
);

const CountTable = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <p className="mb-1 text-sm font-semibold">{title}</p>
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-b"><td className="py-1.5">{r.name}</td><td className="text-right font-num">{fmt(r.value, "num")}</td></tr>
        ))}
        {!rows.length && <tr><td className="py-2 text-muted-foreground">Tidak ada data</td></tr>}
      </tbody>
    </table>
  </div>
);

export function RnTab({ rn }) {
  const [q, setQ] = useState("");
  const [fb, setFb] = useState(ALL);
  const rows = useMemo(() => rn.reports.filter((r) => (fb === ALL || r.feedback.includes(fb))
    && (!q || `${r.no_laporan} ${r.ulasan || ""} ${r.rayon || ""}`.toLowerCase().includes(q.toLowerCase()))), [rn, q, fb]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {rn.feedback.map(([k, v]) => <span key={k} className="rounded-md border bg-muted px-2 py-1 text-xs">{k}: <b className="font-num">{v}</b></span>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input data-testid="anomali-rn-search" placeholder="Cari no laporan / ulasan…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" />
        <Select value={fb} onValueChange={setFb}>
          <SelectTrigger data-testid="anomali-rn-feedback-filter" className="h-9 w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua jenis feedback</SelectItem>
            {rn.feedback.map(([k]) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <span data-testid="anomali-rn-count" className="self-center text-xs text-muted-foreground">{rows.length} laporan</span>
      </div>
      <div className="max-h-[50vh] overflow-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="p-2">No Laporan</th><th className="p-2">Bulan</th><th className="p-2">Rating</th><th className="p-2">Feedback</th>
            <th className="p-2">Ulasan</th><th className="p-2">Sumber</th><th className="p-2">Jenis</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.no_laporan || r.ulasan} data-testid="anomali-rn-row" className="border-b align-top">
                <td className="p-2 font-num text-xs">{r.no_laporan || "-"}</td>
                <td className="p-2 whitespace-nowrap">{r.bulan}</td>
                <td className="p-2 text-center font-num">{r.rating ?? "-"}</td>
                <td className="p-2 text-xs">{r.feedback.join(", ") || "-"}</td>
                <td className="p-2 min-w-[260px] text-xs whitespace-pre-line">{r.ulasan || "-"}</td>
                <td className="p-2 whitespace-nowrap text-xs">{r.sumber || "-"}</td>
                <td className="p-2 whitespace-nowrap text-xs">{r.jenis || "-"}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Tidak ada laporan</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LuTab = ({ lu }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-3">
      <Stat label="Total Laporan Berulang" value={lu.total} testid="anomali-lu-total" />
      <Stat label="Marking" value={lu.marking} testid="anomali-lu-marking" />
      <Stat label="Non Marking" value={lu.non_marking} testid="anomali-lu-nonmarking" />
    </div>
    <div className="grid gap-6 sm:grid-cols-2">
      <CountTable title="Per Bulan" rows={lu.per_bulan} testid="anomali-lu-per-bulan" />
      <CountTable title="Per Rayon" rows={lu.per_rayon} testid="anomali-lu-per-rayon" />
    </div>
  </div>
);

export const AnomaliDialog = ({ unit, params, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!unit) return;
    setData(null); setError(null);
    api.get("/anomali/detail", { params: { ...params, unit } }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);
  return (
    <Dialog open={!!unit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="anomali-dialog" className="max-w-5xl w-[95vw] min-w-0 grid-cols-1 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading">Detail Anomali WO — {unit}</DialogTitle>
          <DialogDescription>{data?.period_label}</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {!data && !error && <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />}
        {data && (
          <Tabs defaultValue="rn" className="min-w-0">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger data-testid="anomali-tab-rn" value="rn">Rating Negatif ({data.rn.total})</TabsTrigger>
              <TabsTrigger data-testid="anomali-tab-lu" value="lu">Laporan Berulang ({data.lu.total})</TabsTrigger>
            </TabsList>
            <TabsContent value="rn"><RnTab rn={data.rn} /></TabsContent>
            <TabsContent value="lu"><LuTab lu={data.lu} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
